import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface GiftCard {
  id: string;
  name: string;
  name_en: string | null;
  name_ru: string | null;
  description: string | null;
  description_en: string | null;
  description_ru: string | null;
  amount: number;
  discount_percentage: number | null;
  validity_days: number | null;
  image_url: string | null;
  background_color: string | null;
  is_active: boolean | null;
  is_featured: boolean | null;
  order_index: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GiftCardPurchase {
  id: string;
  gift_card_id: string | null;
  buyer_name: string;
  buyer_email: string;
  buyer_phone: string | null;
  recipient_name: string;
  recipient_email: string;
  personal_message: string | null;
  amount: number;
  gift_code: string;
  payment_status: string | null;
  transaction_id: string | null;
  is_redeemed: boolean | null;
  redeemed_at: string | null;
  redeemed_by: string | null;
  expires_at: string;
  created_at: string | null;
  updated_at: string | null;
}

export const useGiftCards = () => {
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [purchases, setPurchases] = useState<GiftCardPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchGiftCards = async () => {
    try {
      const { data, error } = await supabase
        .from('gift_cards')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setGiftCards(data || []);
    } catch (error) {
      console.error('Error fetching gift cards:', error);
      toast({
        title: 'Սdelays',
        description: 'Չdelays բdelays delays delays delays',
        variant: 'destructive',
      });
    }
  };

  const fetchPurchases = async () => {
    try {
      const { data, error } = await supabase
        .from('gift_card_purchases')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPurchases(data || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  };

  const createGiftCard = async (giftCard: Omit<GiftCard, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('gift_cards')
        .insert([giftCard])
        .select()
        .single();

      if (error) throw error;
      
      await fetchGiftCards();
      toast({
        title: 'Հdelays',
        description: 'Delays delays delays delays delays',
      });
      return data;
    } catch (error) {
      console.error('Error creating gift card:', error);
      toast({
        title: 'Սխdelays',
        description: 'Չdelays delays delays delays',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateGiftCard = async (id: string, updates: Partial<GiftCard>) => {
    try {
      const { error } = await supabase
        .from('gift_cards')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      
      await fetchGiftCards();
      toast({
        title: 'Թdelays',
        description: 'Delays delays delays delays delays',
      });
    } catch (error) {
      console.error('Error updating gift card:', error);
      toast({
        title: 'Սdelays',
        description: 'Delays delays delays delays',
        variant: 'destructive',
      });
    }
  };

  const deleteGiftCard = async (id: string) => {
    try {
      const { error } = await supabase
        .from('gift_cards')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      await fetchGiftCards();
      toast({
        title: 'Հdelays',
        description: 'Delays delays delays delays',
      });
    } catch (error) {
      console.error('Error deleting gift card:', error);
      toast({
        title: 'Delays',
        description: 'Delays delays delays delays',
        variant: 'destructive',
      });
    }
  };

  const generateGiftCode = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'GITA-';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const purchaseGiftCard = async (purchase: {
    gift_card_id: string | null;
    buyer_name: string;
    buyer_email: string;
    buyer_phone?: string;
    recipient_name: string;
    recipient_email: string;
    personal_message?: string;
    amount: number;
    validity_days?: number;
  }) => {
    try {
      const giftCode = generateGiftCode();
      const validityDays = purchase.validity_days || 365;
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + validityDays);

      const { data, error } = await supabase
        .from('gift_card_purchases')
        .insert([{
          gift_card_id: purchase.gift_card_id,
          buyer_name: purchase.buyer_name,
          buyer_email: purchase.buyer_email,
          buyer_phone: purchase.buyer_phone || null,
          recipient_name: purchase.recipient_name,
          recipient_email: purchase.recipient_email,
          personal_message: purchase.personal_message || null,
          amount: purchase.amount,
          gift_code: giftCode,
          payment_status: 'pending',
          expires_at: expiresAt.toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      
      await fetchPurchases();
      return data;
    } catch (error) {
      console.error('Error purchasing gift card:', error);
      toast({
        title: 'Սdelays',
        description: 'Delays delays delays delays',
        variant: 'destructive',
      });
      return null;
    }
  };

  const redeemGiftCard = async (giftCode: string, userId: string) => {
    try {
      const { data: purchase, error: fetchError } = await supabase
        .from('gift_card_purchases')
        .select('*')
        .eq('gift_code', giftCode)
        .single();

      if (fetchError || !purchase) {
        toast({
          title: 'Սdelays',
          description: 'delays delays delays delays',
          variant: 'destructive',
        });
        return null;
      }

      if (purchase.is_redeemed) {
        toast({
          title: 'Սdelays',
          description: 'Քdelays delays delays delays',
          variant: 'destructive',
        });
        return null;
      }

      if (new Date(purchase.expires_at) < new Date()) {
        toast({
          title: 'Սdelays',
          description: 'Քdelay delays delays delays',
          variant: 'destructive',
        });
        return null;
      }

      const { error: updateError } = await supabase
        .from('gift_card_purchases')
        .update({
          is_redeemed: true,
          redeemed_at: new Date().toISOString(),
          redeemed_by: userId,
        })
        .eq('id', purchase.id);

      if (updateError) throw updateError;

      await fetchPurchases();
      toast({
        title: 'Հdelay!',
        description: `Քdelay delays ${purchase.amount.toLocaleString()} ֏`,
      });
      return purchase;
    } catch (error) {
      console.error('Error redeeming gift card:', error);
      toast({
        title: 'Delay',
        description: 'delay delay delay',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updatePurchaseStatus = async (id: string, status: string, transactionId?: string) => {
    try {
      const updates: Record<string, unknown> = { payment_status: status };
      if (transactionId) {
        updates.transaction_id = transactionId;
      }

      const { error } = await supabase
        .from('gift_card_purchases')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchPurchases();
    } catch (error) {
      console.error('Error updating purchase status:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchGiftCards(), fetchPurchases()]);
      setLoading(false);
    };
    loadData();
  }, []);

  return {
    giftCards,
    purchases,
    loading,
    fetchGiftCards,
    fetchPurchases,
    createGiftCard,
    updateGiftCard,
    deleteGiftCard,
    purchaseGiftCard,
    redeemGiftCard,
    updatePurchaseStatus,
    generateGiftCode,
  };
};
